import axios from "axios";

// Shared by loginApi's success path and loginMfa so the two success flows
// cannot drift: write the token, commit the profile, mark the status.
const applyLoginSuccess = (commit, data) => {
  localStorage.setItem("access_token", data.accessToken);
  // Refresh token is handled via httpOnly cookies by the backend
  commit("setUserProfile", data.user);
  commit("setLoginApiStatus", "success");
};

const moduleAuth = {
  namespaced: true,
  state: {
    loginApiStatus: "",
    userProfile: {
      uid: 0,
      firstNameTh: "",
      lastNameTh: "",
      email: "",
      mobile: ""
    },
    logOut: false,
    accessToken: null
  },
  mutations: {
    setLoginApiStatus(state, data) {
      state.loginApiStatus = data;
    },
    setUserProfile(state, data) {
      const userProfile = {
        id: data.id,
        lastName: data.lastName,
        firstName: data.firstName,
        email: data.email,
        phone: data.phone
      };
      state.userProfile = userProfile;
    },
    setLogout(state, payload) {
      state.logOut = payload;
    },
    setAccessToken(state, payload) {
      state.accessToken = payload;
    }
  },
  actions: {
    loginApi({ commit }, payload) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/api/login`, payload)
          .then(res => {
            if (res && res.data && res.data.mfaRequired) {
              // MFA challenge: write nothing to localStorage, commit nothing.
              resolve({
                outcome: "mfa_required",
                mfaToken: res.data.mfaToken,
                methods: res.data.methods
              });
              return;
            }

            if (res && res.data) {
              applyLoginSuccess(commit, res.data.data);
            }

            resolve({ outcome: "success" });
          })
          .catch(err => {
            if (err.response && err.response.status === 423) {
              // Lockout: resolve (not reject) so the view can show the
              // unlock-time message instead of falling into the generic
              // error-handling catch.
              resolve({
                outcome: "locked",
                lockedUntil:
                  err.response.data &&
                  err.response.data.data &&
                  err.response.data.data.lockedUntil
              });
              return;
            }

            commit("setLoginApiStatus", "failed");
            reject(err);
          });
      });
    },
    loginMfa({ commit }, { mfaToken, code, recoveryCode, trustDevice }) {
      return new Promise((resolve, reject) => {
        const body = { mfaToken };
        if (code !== undefined) {
          body.code = code;
        }
        if (recoveryCode !== undefined) {
          body.recoveryCode = recoveryCode;
        }
        if (trustDevice) {
          body.trustDevice = true;
        }

        axios
          .post(`/api/login/mfa`, body)
          .then(res => {
            if (res && res.data) {
              applyLoginSuccess(commit, res.data.data);
            }

            resolve({ outcome: "success" });
          })
          .catch(err => {
            // Let 401 (wrong code) / 423 (locked) reject so the view can
            // show the corresponding inline message.
            reject(err);
          });
      });
    },
    passkeyLoginStart({ commit }) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/api/login/passkey/start`)
          .then(res => {
            resolve(res.data);
          })
          .catch(err => {
            // Let 401/501 reject so the view can react.
            reject(err);
          });
      });
    },
    passkeyLoginFinish({ commit }, { response, authenticationToken, rememberMe }) {
      return new Promise((resolve, reject) => {
        const body = { authenticationToken };
        if (response !== undefined) {
          body.response = response;
        }
        if (rememberMe !== undefined) {
          body.rememberMe = rememberMe;
        }

        axios
          .post(`/api/login/passkey/finish`, body)
          .then(res => {
            if (res && res.data) {
              applyLoginSuccess(commit, res.data.data);
            }

            resolve({ outcome: "success" });
          })
          .catch(err => {
            // Let 401 (failed assertion) / 501 (tenant unsupported) reject so
            // the view can show the corresponding message.
            reject(err);
          });
      });
    },
    async userLogout({ commit }) {
      const response = await axios
        .post(`/api/logout`)
        .catch(err => {
          console.error(err);
        });

      if (response && response.data) {
        commit("setLogout", true);
        localStorage.removeItem("access_token");
        // Refresh token cookie is cleared by the backend
      } else {
        commit("setLogout", false);
      }
    },
    refreshToken({ commit }) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/api/token`)
          .then(res => {
            const { status } = res;
            if (status == 200 || status == 204) {
              // Handle different response structures
              const tokenData = res.data.data || res.data;
              if (tokenData.accessToken) {
                localStorage.setItem("access_token", tokenData.accessToken);
                commit("setAccessToken", tokenData.accessToken);
              } else {
                console.warn("No access token in refresh response");
              }
            }
            resolve(res);
          })
          .catch(err => {
            reject(err);
          });
      });
    },

    checkUserLogin(payload) {
      return new Promise((resolve, reject) => {
        const endPoint =
          payload && payload.clinicUrl && payload.branchUrl
            ? `/api/${payload.clinicUrl}/${payload.branchUrl}`
            : `/api/user/checkLogin`;

        axios
          .get(endPoint)
          .then(res => {
            resolve(res);
          })
          .catch(err => {
            reject(err);
          });
      });
    }
  },
  getters: {
    getLoginApiStatus(state) {
      return state.loginApiStatus;
    },
    getUserProfile(state) {
      return state.userProfile;
    },
    getLogout(state) {
      return state.logOut;
    },
    getAccessToken(state) {
      return state.accessToken;
    }
  }
};

export default moduleAuth;
